import { z } from 'zod';
import type { AccountTransaction } from './signable';
import type { NetworkType } from './network';

const UTC_DAY_MILLISECONDS = 24 * 60 * 60 * 1_000;

const executionDateFiltersSchema = z.object({
  executedFrom: z.iso.date().optional(),
  executedThrough: z.iso.date().optional(),
});
const validExecutionDateRange = ({
  executedFrom,
  executedThrough,
}: z.input<typeof executionDateFiltersSchema>) =>
  !executedFrom || !executedThrough || executedFrom <= executedThrough;
const executionDateRangeError = {
  message: 'The end date must be on or after the start date.',
  path: ['executedThrough'],
};

/** Account and inclusive UTC execution-date filters for Historic. */
export const transactionHistoryFiltersSchema = executionDateFiltersSchema
  .extend({ accountId: z.uuidv4().optional() })
  .refine(validExecutionDateRange, executionDateRangeError);

/** Repeated query keys arrive as an array; a single account arrives as a string. */
export const transactionExportFiltersSchema = executionDateFiltersSchema
  .extend({
    accountIds: z
      .union([z.uuidv4(), z.array(z.uuidv4())])
      .transform((ids) => [...new Set(typeof ids === 'string' ? [ids] : ids)])
      .optional(),
  })
  .refine(validExecutionDateRange, executionDateRangeError);

export type TransactionHistoryFilters = z.infer<
  typeof transactionHistoryFiltersSchema
>;
export type TransactionExportFilters = z.infer<
  typeof transactionExportFiltersSchema
>;

/** Convert calendar dates to a half-open UTC range, including the entire end day. */
export function getTransactionHistoryDateRange(
  filters: Pick<TransactionHistoryFilters, 'executedFrom' | 'executedThrough'>
): { from?: Date; before?: Date } {
  return {
    ...(filters.executedFrom
      ? { from: new Date(`${filters.executedFrom}T00:00:00.000Z`) }
      : {}),
    ...(filters.executedThrough
      ? {
          before: new Date(
            Date.parse(`${filters.executedThrough}T00:00:00.000Z`) +
              UTC_DAY_MILLISECONDS
          ),
        }
      : {}),
  };
}

/** CSV values are independent of database models and retain exact decimal amounts. */
export interface TransactionExportRow {
  transactionDate: string;
  transactionHash: string;
  fromWalletAddress: string;
  toWalletAddress: string;
  direction: 'Sent' | 'Received';
  tokenName: string;
  tokenQuantity: string;
  description: string;
}

/** A confirmed indexed token movement, independent of MLS proposals and database models. */
export interface IndexedTokenMovement {
  id: string;
  direction: 'sent' | 'received';
  networkId: NetworkType;
  transactionHash: string;
  executedAt: string;
  fromAddress: string;
  toAddress: string;
  account: { id: string; name: string };
  token: {
    name: string;
    symbol: string;
    logoUrl: string | null;
    quantity: string;
  };
  description: string;
}

/** Historic keeps proposal actions and read-only indexed movements as distinct row types. */
export type TransactionHistoryItem =
  | { kind: 'proposal'; id: string; transaction: AccountTransaction }
  | { kind: 'movement'; id: string; movement: IndexedTokenMovement };
